/**
 * PDP V2 — JavaScript
 * 
 * CURSOR INSTRUCTIONS:
 * ====================
 * This file handles all interactive behavior for the PDP V2 template.
 * It must work alongside the existing Empire theme JS without conflicts.
 * All selectors use data-attributes and .pdp-v2 prefixed classes to avoid collisions.
 * 
 * MODULES (all delegated via single document click listener):
 * 1. Kit Selector — variant switching via radio cards
 * 2. "What's included" — collapsible kit contents
 * 3. Accordions — toggle open/close
 * 4. Coverage Guide — tile selection + recommendation display
 * 5. Quantity — plus/minus buttons
 * 6. Scroll-to-kit — CTA deep links
 * 
 * ARCHITECTURE:
 * - ONE click listener on document — survives any DOM replacement
 *   (Section Rendering API, theme editor, AJAX re-renders)
 * - ZERO cached DOM refs — every element is queried fresh inside its handler
 * - Product JSON + variant data are the only things cached (parsed data, not DOM)
 * 
 * IMPORTANT:
 * - The product JSON is available in #pdp-v2-product-json
 * - The hidden input[name="id"] inside the product form controls which variant is submitted
 * - When a kit card is clicked, update: hidden input, price display, ATC button text, active state
 * - Gallery, Reviews Carousel, and FBT are handled by their own section scripts
 */

(function () {
  'use strict';

  // ═══ PRODUCT DATA (parsed JSON — safe to cache, not DOM) ═══
  var productJson = document.getElementById('pdp-v2-product-json');
  if (!productJson) return;

  var product = JSON.parse(productJson.textContent);
  var variants = product.variants;

  var sectionEl = document.querySelector('.pdp-v2[data-section-id]');
  var hasSellingPlans = sectionEl && parseInt(sectionEl.dataset.hasSellingPlans, 10) > 0;
  var isPreorder = sectionEl && sectionEl.dataset.isPreorder === 'true';

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // ═══ COVERAGE GUIDE DATA ═══
  // Populated by Liquid from the coverage_calculator metaobject.
  // If the metafield isn't set the JSON block and the accordion are both absent.
  var coverageDataEl = document.getElementById('pdp-v2-coverage-data');
  var coverageData = coverageDataEl ? JSON.parse(coverageDataEl.textContent) : null;



  // ═══ ANIMATION HELPERS ═══
  function animateOpen(el) {
    el.style.height = '0';
    requestAnimationFrame(function () {
      el.style.height = el.scrollHeight + 'px';
      el.classList.add('is-open');
    });
    el.addEventListener('transitionend', function handler() {
      if (el.classList.contains('is-open')) el.style.height = 'auto';
      el.removeEventListener('transitionend', handler);
    });
  }

  function animateClose(el) {
    el.style.height = el.scrollHeight + 'px';
    requestAnimationFrame(function () {
      el.style.height = '0';
      el.classList.remove('is-open');
    });
  }

  // ═══ SINGLE DELEGATED CLICK HANDLER ON document ═══
  document.addEventListener('click', function (e) {

    // ─── 1. KIT SELECTOR (variant radio cards) ───
    var card = e.target.closest('.pdp-v2__kit-card');
    if (card && !e.target.closest('[data-toggle-included]')) {
      var variantId = card.dataset.variantId;
      var variantPrice = parseInt(card.dataset.variantPrice, 10);
      var compareAtPrice = parseInt(card.dataset.variantCompareAtPrice, 10) || 0;
      var isAvailable = card.dataset.variantAvailable === 'true';
      var onSale = compareAtPrice > variantPrice;

      // Live DOM lookups
      var allCards = document.querySelectorAll('.pdp-v2__kit-card');
      var variantInput = document.querySelector('[data-variant-input]');
      var priceDisplay = document.querySelector('[data-pdp-price]');
      var comparePriceEl = document.querySelector('[data-pdp-compare-price]');
      var saveBadge = document.querySelector('[data-pdp-save-badge]');
      var atcBtn = document.querySelector('[data-pdp-atc]');
      var stockEl = document.querySelector('[data-pdp-stock-status]');

      // Update active state on all cards
      allCards.forEach(function (c) {
        c.classList.remove('is-active');
        var radio = c.querySelector('.pdp-v2__kit-radio');
        if (radio) radio.innerHTML = '';
      });
      card.classList.add('is-active');
      var activeRadio = card.querySelector('.pdp-v2__kit-radio');
      if (activeRadio) activeRadio.innerHTML = '<div class="pdp-v2__kit-radio-fill"></div>';

      // Update hidden variant input
      if (variantInput) variantInput.value = variantId;

      // Update payment terms (installment line)
      var paymentTermsInput = document.querySelector('[data-payment-terms-variant]');
      if (paymentTermsInput) {
        paymentTermsInput.value = variantId;
        paymentTermsInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Update main price
      if (priceDisplay) {
        priceDisplay.innerHTML = '<span class="sr-only">' + (onSale ? 'Sale price ' : '') + '</span>' + formatMoney(variantPrice);
      }

      // Update compare-at price + save badge
      if (comparePriceEl) {
        if (onSale) {
          comparePriceEl.innerHTML = '<span class="sr-only">Original price </span>' + formatMoney(compareAtPrice);
          comparePriceEl.classList.remove('hidden');
        } else {
          comparePriceEl.classList.add('hidden');
        }
      }
      if (saveBadge) {
        if (onSale) {
          var pct = Math.round((compareAtPrice - variantPrice) / compareAtPrice * 100);
          saveBadge.textContent = 'Save ' + pct + '%';
          saveBadge.classList.remove('hidden');
        } else {
          saveBadge.classList.add('hidden');
        }
      }

      // Update stock status
      if (stockEl) {
        if (isAvailable) {
          stockEl.className = 'pdp-v2__urgency flex items-center gap-2 text-[13px] font-medium text-pdp-green mb-3';
          stockEl.innerHTML = '<span class="pdp-v2__urgency-dot w-2 h-2 rounded-full bg-pdp-green animate-[pdp-v2-pulse_2s_infinite]"></span>In Stock — Ships in 1-2 business days';
        } else {
          stockEl.className = 'pdp-v2__urgency flex items-center gap-2 text-[13px] font-medium text-pdp-red mb-3';
          stockEl.innerHTML = '<span class="pdp-v2__urgency-dot w-2 h-2 rounded-full bg-pdp-red"></span>Out of Stock';
        }
      }

      // Update ATC button state
      if (atcBtn) {
        if (!isAvailable) {
          atcBtn.disabled = true;
          atcBtn.classList.add('opacity-50', 'cursor-not-allowed');
          atcBtn.classList.remove('bg-[#F04F36]', 'cursor-pointer', 'hover:bg-[#c23b2a]');
          atcBtn.innerHTML = 'Sold Out';
        } else if (isPreorder) {
          atcBtn.disabled = false;
          atcBtn.classList.remove('opacity-50', 'cursor-not-allowed');
          atcBtn.classList.add('bg-[#F04F36]', 'cursor-pointer', 'hover:bg-[#c23b2a]');
          atcBtn.innerHTML = 'Pre-order — <span data-pdp-atc-price>' + formatMoney(variantPrice) + '</span>';
        } else {
          atcBtn.disabled = false;
          atcBtn.classList.remove('opacity-50', 'cursor-not-allowed');
          atcBtn.classList.add('bg-[#F04F36]', 'cursor-pointer', 'hover:bg-[#c23b2a]');
          atcBtn.innerHTML = 'Add to Cart — <span data-pdp-atc-price>' + formatMoney(variantPrice) + '</span>';
        }
      }

      // Toggle dynamic checkout wrapper (respects selling plan + pre-order guards)
      var dynamicCheckout = document.querySelector('[data-dynamic-checkout]');
      if (dynamicCheckout) {
        dynamicCheckout.style.display = (isAvailable && !hasSellingPlans && !isPreorder) ? '' : 'none';
      }

      // Toggle Sold Out badge (always in DOM, toggle hidden class)
      var soldOutBadge = document.querySelector('[data-pdp-sold-out-badge]');
      if (soldOutBadge) {
        soldOutBadge.classList.toggle('hidden', isAvailable);
      }

      // Toggle Notify Me button per variant
      var notifyWrap = document.querySelector('[data-pdp-notify-wrap]');
      if (notifyWrap) {
        notifyWrap.style.display = isAvailable ? 'none' : '';
      }

      // Switch gallery image to variant's featured media
      var matchedVariant = variants.find(function (v) { return String(v.id) === String(variantId); });
      if (matchedVariant && matchedVariant.featured_media) {
        var mediaId = matchedVariant.featured_media.id;
        var thumb = document.querySelector('[data-gallery-thumbnail][data-media="' + mediaId + '"]');
        if (thumb) thumb.click();
      }

      // Update URL without reload
      var url = new URL(window.location);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url);
      return;
    }

    // ─── 2. "WHAT'S INCLUDED" TOGGLE ───
    var toggleBtn = e.target.closest('[data-toggle-included]');
    if (toggleBtn) {
      e.preventDefault();
      var parentCard = toggleBtn.closest('.pdp-v2__kit-card');
      var list = parentCard ? parentCard.querySelector('.pdp-v2__kit-included') : null;
      var arrow = toggleBtn.querySelector('span');
      if (!list) return;

      if (list.classList.contains('is-open')) {
        animateClose(list);
        if (arrow) arrow.textContent = '\u25be';
      } else {
        // Temporarily display to measure, then animate from 0
        list.style.display = 'grid';
        var h = list.scrollHeight;
        list.style.display = '';
        list.style.height = '0';
        requestAnimationFrame(function () {
          list.style.height = h + 'px';
          list.classList.add('is-open');
        });
        list.addEventListener('transitionend', function handler() {
          if (list.classList.contains('is-open')) list.style.height = 'auto';
          list.removeEventListener('transitionend', handler);
        });
        if (arrow) arrow.textContent = '\u25b4';
      }
      return;
    }

    // ─── 3. ACCORDIONS ───
    var accordionToggle = e.target.closest('[data-accordion-toggle]');
    if (accordionToggle) {
      var item = accordionToggle.closest('.pdp-v2__accordion-item');
      if (!item) return;
      var content = item.querySelector('.pdp-v2__accordion-content');
      if (!content) return;
      var iconOpen = accordionToggle.querySelector('.pdp-v2__accordion-icon-open');
      var iconClose = accordionToggle.querySelector('.pdp-v2__accordion-icon-close');
      var label = accordionToggle.querySelector('span:first-child');
      var isOpen = content.classList.contains('is-open');

      if (isOpen) {
        animateClose(content);
        if (iconOpen) iconOpen.classList.remove('hidden');
        if (iconClose) iconClose.classList.add('hidden');
        if (label) label.classList.remove('text-pdp-red');
      } else {
        animateOpen(content);
        if (iconOpen) iconOpen.classList.add('hidden');
        if (iconClose) iconClose.classList.remove('hidden');
        if (label) label.classList.add('text-pdp-red');
      }
      return;
    }

    // ─── 4. COVERAGE GUIDE ───
    var coverageTile = e.target.closest('.pdp-v2__coverage-tile');
    if (coverageTile && coverageData) {
      // Live lookups for all coverage elements
      var allTiles = document.querySelectorAll('.pdp-v2__coverage-tile');
      var recBox = document.querySelector('[data-coverage-rec]');
      var recText = document.querySelector('[data-coverage-rec-text]');
      var wasActive = coverageTile.classList.contains('is-active');
      var boxIsOpen = recBox && recBox.classList.contains('is-open');

      allTiles.forEach(function (t) { t.classList.remove('is-active'); });

      if (wasActive) {
        if (recBox) animateClose(recBox);
        return;
      }

      coverageTile.classList.add('is-active');
      var type = coverageTile.dataset.coverage;
      var text = coverageData[type];

      if (text && recBox && recText) {
        recText.innerHTML = text;
        if (!boxIsOpen) animateOpen(recBox);
      }
      return;
    }

    // ─── 5. QUANTITY — MINUS ───
    // Uses the existing quantity-selector snippet's data attributes
    var minusBtn = e.target.closest('[data-quantity-minus]');
    if (minusBtn) {
      var qtyWrap = minusBtn.closest('[data-quantity-selector]');
      var input = qtyWrap ? qtyWrap.querySelector('[data-quantity-input]') : document.querySelector('[data-quantity-input]');
      if (input) {
        var val = parseInt(input.value, 10) || 1;
        if (val > 1) input.value = val - 1;
      }
      return;
    }

    // ─── 5. QUANTITY — PLUS ───
    var plusBtn = e.target.closest('[data-quantity-plus]');
    if (plusBtn) {
      var qtyWrap2 = plusBtn.closest('[data-quantity-selector]');
      var input2 = qtyWrap2 ? qtyWrap2.querySelector('[data-quantity-input]') : document.querySelector('[data-quantity-input]');
      if (input2) {
        var val2 = parseInt(input2.value, 10) || 1;
        input2.value = val2 + 1;
      }
      return;
    }

    // ─── 6. SCROLL TO KIT (from FBT/review CTAs) ───
    var scrollLink = e.target.closest('[data-scroll-to-kit]');
    if (scrollLink) {
      e.preventDefault();
      var kitName = scrollLink.dataset.scrollToKit;
      var kitSection = document.querySelector('.pdp-v2__kit-selector');
      if (kitSection) {
        kitSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Fresh query for cards — never use stale refs
        var freshCards = document.querySelectorAll('.pdp-v2__kit-card');
        freshCards.forEach(function (c) {
          if (c.dataset.variantTitle && c.dataset.variantTitle.includes(kitName)) {
            setTimeout(function () { c.click(); }, 600);
          }
        });
      }
      return;
    }

  });

  // ═══ GALLERY ═══
  // Handled by Empire theme's ProductGallery (data-section-type="static-product").
  // Variant → gallery switching triggers via thumbnail click in kit selector above.

  // ═══ REVIEWS CAROUSEL ═══
  // Handled by Swiper via l-swiper-carousel in pdp-v2-reviews.liquid

  // ═══ FBT ═══
  // Handled entirely by inline script in pdp-v2-fbt.liquid

  // ═══ STICKY ATC BAR ═══
  var stickyBar = document.querySelector('[data-pdp-sticky-atc]');
  var mainAtcBtn = document.querySelector('[data-pdp-atc]');
  if (stickyBar && mainAtcBtn) {
    var checkSticky = function () {
      var rect = mainAtcBtn.getBoundingClientRect();
      var pastButton = rect.bottom < 0;
      stickyBar.classList.toggle('is-visible', pastButton);
    };
    checkSticky();
    window.addEventListener('scroll', checkSticky, { passive: true });

    var stickyBtn = stickyBar.querySelector('[data-pdp-sticky-btn]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function () {
        mainAtcBtn.click();
      });
    }

    document.addEventListener('click', function (e) {
      var card = e.target.closest('.pdp-v2__kit-card');
      if (card && !e.target.closest('[data-toggle-included]')) {
        var stickyPrice = stickyBar.querySelector('[data-pdp-sticky-price]');
        var stickySubmit = stickyBar.querySelector('[data-pdp-sticky-btn]');
        var price = parseInt(card.dataset.variantPrice, 10);
        var available = card.dataset.variantAvailable === 'true';

        if (stickyPrice) stickyPrice.textContent = formatMoney(price);
        if (stickySubmit) {
          stickySubmit.disabled = !available;
          stickySubmit.textContent = available ? 'Add to Cart' : 'Sold Out';
        }
      }
    });
  }

})();
